import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { resetPassword } from "../../api/auth";
import AppButton from "../../components/AppButton";
import { ROUTES } from "../config";
import AuthFlowTitle from "./components/AuthFlowTitle";
import ErrorMessage from "./components/ErrorMessage";
import FormLoginInput from "./components/FormLoginInput";
import MFAForm from "./components/MFAForm";

// Zod schema for form validation
const resetPasswordSchema = z
    .object({
        username: z.string().min(1, "User name is required"),
        password: z
            .string()
            .min(8, "Password must be at least 8 characters")
            .regex(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/,
                "Password must contain at least one capital letter, at least one lowercase letter, at least one special character, at least one digit, at least eight characters"
            ),
        confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: "Passwords don't match",
        path: ["confirmPassword"],
    });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

function ResetPasswordPage() {
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState<"choose-password" | "verify-otp">(
        "choose-password"
    );

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        getValues,
    } = useForm<ResetPasswordFormData>({
        resolver: zodResolver(resetPasswordSchema),
    });
    const watchUsername = watch("username");

    const onSubmit = async (data: ResetPasswordFormData) => {
        try {
            setErrorMessage(null);
            setLoading(true);
            const res = await resetPassword(data.username, data.password);
            if (!res || res.error) {
                throw new Error(res?.error || "Failed to reset password");
            }
            setStep("verify-otp");
        } catch (error) {
            console.error("Reset password error:", error);
            setErrorMessage(
                error instanceof Error
                    ? error.message
                    : "An unexpected error occurred"
            );
        } finally {
            setLoading(false);
        }
    };

    const sendCodeAgain = async () => {
        try {
            const res = await resetPassword(
                ...getValues(["username", "password"])
            );
            if (!res || res.error) {
                throw new Error(res?.error || "Failed to send code again");
            }
        } catch (error) {
            console.error("Error sending code again:", error);
            alert("Failed to send code again");
        }
    };

    return step === "choose-password" ? (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className="h-full flex flex-col gap-5"
        >
            <AuthFlowTitle>Reset Password</AuthFlowTitle>

            <FormLoginInput
                id="username"
                type="username"
                label="User Name"
                {...register("username")}
                placeholder="Enter your user name"
                autoComplete={true}
                error={errors.username?.message}
            />

            <FormLoginInput
                id="password"
                label="New Password"
                {...register("password")}
                type="password"
                placeholder="Enter your new password"
                autoComplete={false}
                error={errors.password?.message}
                infoTooltip={
                    <div className="w-max">
                        <p className="font-bold mb-1">Password requirements:</p>
                        <ul className="list-disc list-inside ps-1.5 font-semibold leading-relaxed">
                            <li>At least one capital letter</li>
                            <li>At least one lowercase letter</li>
                            <li>At least one special character</li>
                            <li>At least six characters</li>
                            <li>At least one digit</li>
                        </ul>
                    </div>
                }
            />

            <FormLoginInput
                id="confirmPassword"
                label="Retype Password"
                {...register("confirmPassword")}
                type="password"
                placeholder="Retype your new password"
                autoComplete={false}
                error={errors.confirmPassword?.message}
            />

            <div className="mt-auto flex flex-col gap-2">
                <ErrorMessage message={errorMessage} />
                <AppButton type="submit" loading={loading}>
                    Reset
                </AppButton>
                <span className="text-sm xl:text-base">
                    <span className="font-bold">Note:</span> Upon completing the
                    password reset process you will receive a confirmation
                    email.
                </span>
            </div>
        </form>
    ) : (
        <MFAForm
            username={watchUsername}
            onSuccess={async () => {
                navigate(`/${ROUTES.LOGIN.path}`);
            }}
            onError={(errorCode) => {
                if (errorCode === "password-previously-used") {
                    setErrorMessage("Password has previously been used.");
                    setStep("choose-password");
                }
            }}
            title="Confirm Password Reset"
            submitButtonText="Confirm"
            sendCodeAgain={sendCodeAgain}
            codeSentVia="email"
        />
    );
}

export default ResetPasswordPage;
